/**
* @author zjc[beica1@outook.com]
* @date 2021/10/14 20:01
* @description
*   TheChart.vue of WeTrade
*/
<template>
<div class="p-r">
  <TheResolutionBar @change="changeResolution">
    <div class="rotate pr-8" @click="rotateScreen">
      <Pic src="trade/rotate" width="24" height="24" />
    </div>
  </TheResolutionBar>
  <RatioFixedArea ratio="0.52" style="overflow: hidden">
    <div id="hc_main_chart" class="fill">
      <ThePositionLines :fy="fy" :refresh-id="refreshId" />
      <ThePriceLabel :price="price" :position="position" />
      <TheCrosshair :crosshair="crosshair" />
    </div>
  </RatioFixedArea>
  <Line />
  <TheStudyBar :studies="overlayStudies" @change="changeOverlayStudy"
    :selected="defaultOverlayStudy">
    <router-link to="/symbol/setIndicators" custom #="{navigate}">
      <div class="set-btn df-middle df-center" @click="navigate">
        <Icon name="settings" sm />
      </div>
    </router-link>
  </TheStudyBar>
  <RatioFixedArea ratio="0.208">
    <!--<RatioFixedArea ratio="0.508">-->
    <div id="ch_study_chart" class="fill">
      <TheHorizontalScan :crosshair="crosshair" />
    </div>
  </RatioFixedArea>
  <Line />
  <TheStudyBar :studies="panelStudies" @change="changePanelStudy" />
  <Pic src="to_right" id="restore" width="24" height="24" />
</div>
</template>

<script lang="ts">
import { marketFeed } from '@/common/datafeed'
import Line from '@/components/Line.vue'
import Pic from '@/components/Pic.vue'
import Icon from '@/components/Icon.vue'
import { events } from '@/config'
import { rotate } from '@/modules/app/app'
import { useDrawing } from '@/modules/market/components/chart/chart'
import TheCrosshair from '@/modules/market/components/chart/TheCrosshair.vue'
import TheHorizontalScan from '@/modules/market/components/chart/TheHorizontalScan.vue'
import ThePositionLines from '@/modules/market/components/chart/ThePositionLines.vue'
import ThePriceLabel from '@/modules/market/components/chart/ThePriceLabel.vue'
import {
  panelStudies, ResolutionConfig,
  StudyConfig, readResolutions, BarTypes,
} from '@/modules/market/components/chart/chartConfig'
import datafeed from '@/modules/market/components/chart/datafeed'
import TheResolutionBar from '@/modules/market/components/chart/TheResolutionBar.vue'
import TheStudyBar from '@/modules/market/components/chart/TheStudyBar.vue'
import { useOverlayStudies } from '@/modules/market/market'
import { readLines } from '@/modules/market/market.api'
import RatioFixedArea from 'common/RatioFixedArea.vue'
import { off, on } from 'essential/tools/event'
import HappyChart, { ChartType } from 'happychart'
import { ChartPointer } from 'happychart/interface/IChart'
import { defineComponent, onBeforeUnmount, onMounted, shallowRef, watchEffect } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import Chart from 'happychart/chart/Chart'
import * as R from 'ramda'

export default defineComponent({
  name: 'TheChart',
  components: {
    ThePositionLines,
    TheHorizontalScan,
    TheCrosshair,
    Pic,
    ThePriceLabel,
    TheStudyBar,
    Line,
    RatioFixedArea,
    TheResolutionBar,
    Icon,
  },
  setup () {
    const router = useRouter()
    const route = useRoute()
    let hc: HappyChart | null = null
    let mainChart: Chart | null = null
    let studyChart: Chart | null = null
    const price = shallowRef('')
    const position = shallowRef<{ top: string; left: string }>({
      top: '0px',
      left: '0px',
    })
    const crosshair = shallowRef<ChartPointer | null>(null)
    const chartGridColor = 'rgba(244,246, 248, 0.5)'
    const refreshId = shallowRef(Math.random())
    const resolutionValue = shallowRef(BarTypes.K_1)

    const [overlayStudies, defaultOverlayStudy, changeOverlayStudy] = useOverlayStudies(config => {
      mainChart?.removeAllStudies()
      if (!R.isEmpty(config.input)) {
        mainChart?.addStudy(config.value, config.input)
      }
    })

    let fy = (price: number) => {
      return mainChart?.yAxis.fy(price) ?? 0
    }

    const { draw } = useDrawing()

    const setup = (resolution: ResolutionConfig) => {
      hc = new HappyChart({
        crosshair: true,
        restoreButton: '#restore',
        periodicity: resolution.period,
        container: 'hc_main_chart',
        datafeed: datafeed,
        yAxis: {
          borderColor: chartGridColor,
          lineColor: chartGridColor,
        },
        xAxis: {
          borderColor: chartGridColor,
          lineColor: chartGridColor,
        },
      })
      resolutionValue.value = resolution.value

      hc.dataMaster.monitor('extent', () => {
        fy = (price: number) => {
          return mainChart?.yAxis.fy(price) ?? 0
        }
      })

      const symbol = route.params.code as string

      mainChart = hc.loadChart(symbol, {
        type: ChartType.CANDLE,
        layout: {
          whitespace: {
            right: 80,
          },
          candleWidth: 8,
        },
      })

      mainChart.onCrosshairMove((data: ChartPointer | null) => {
        crosshair.value = data
      })

      mainChart.onTick((bar, x, y) => {
        refreshId.value = Math.random()
        price.value = bar.c.toString()
        position.value = {
          top: `${y - 7}px`,
          left: '0px',
        }
      })

      if (defaultOverlayStudy) {
        mainChart.addStudy(defaultOverlayStudy.value, defaultOverlayStudy.input)
      }

      studyChart = hc.loadChart(symbol, [], {
        container: 'ch_study_chart',
        showChart: false,
        layout: {
          whitespace: {
            right: 80,
          },
          candleWidth: 8,
        },
        yAxis: {
          showBorder: false,
          showTicks: false,
        },
        xAxis: {
          display: false,
        },
      })

      const defaultPanelStudy = R.find(s => s?.default ?? false, panelStudies) ?? panelStudies[0]
      studyChart.addStudy(defaultPanelStudy.value, defaultPanelStudy.input)

      mainChart.sync(studyChart)
      studyChart.sync(mainChart)

      watchEffect(() => {
        const code = route.params.code
        if (code && resolutionValue.value) {
          readLines({ code: route.params.code })
            .then(resp => draw(
              mainChart,
              R.filter(R.propEq('kLineType', resolutionValue.value), resp),
            ))
        }
      })
    }

    onMounted(() => {
      const defaultPeriodicity = R.find(r => r?.default ?? false, readResolutions())

      if (!defaultPeriodicity) {
        return
      }

      setTimeout(() => {
        setup(defaultPeriodicity)
      }, 0)
    })

    const changeResolution = (r: ResolutionConfig) => {
      resolutionValue.value = r.value
      if (hc) {
        hc.setPeriodicity(r.period)
      } else {
        setup(r)
      }
    }

    const changePanelStudy = (study: StudyConfig, done: (item: StudyConfig) => void) => {
      studyChart?.removeAllStudies()
      studyChart?.addStudy(study.value, study.input)
      done(study)
    }

    const connect = () => {
      const code = route.params.code as string

      if (code) {
        marketFeed.subscribe([code])
        hc?.loadChartData()
      }
    }

    on(events.activated, connect)

    onBeforeUnmount(() => {
      off(events.activated, connect)
      hc?.destroy()
    })

    const toFullscreenView = () => {
      router.push({
        path: '/symbolRotate/' + route.params.code,
      })
    }

    const rotateScreen = () => {
      // dir : vertical horizontal
      rotate('horizontal', () => {
        setTimeout(() => {
          router.back()
        }, 200)
      }).then(toFullscreenView)
    }

    return {
      defaultOverlayStudy: defaultOverlayStudy.value,
      overlayStudies,
      panelStudies,
      price,
      crosshair,
      position,
      refreshId,
      changeResolution,
      changeOverlayStudy,
      changePanelStudy,
      rotateScreen,
      fy,
    }
  },
})
</script>

<style scoped>
.set-btn {
  height: 24px;
  width: 24px;
  background-color: var(--color-light);
}

#restore {
  display: none;
  position: absolute;
  right: 48px;
  margin-top: 46%;
  top: 0;
}
</style>
